import { Fragment, type ReactNode } from 'react';
import { NavIcon } from './NavIcon.js';

/**
 * A breadcrumb step. With `href`, it renders as a link; `onNavigate` then handles a
 * plain click in-app (router navigation) while modified clicks keep the browser's
 * default (open in new tab). The last crumb is the current page and never links.
 */
export interface Crumb {
  label: string;
  href?: string;
  onNavigate?: () => void;
}

export interface TopBarProps {
  breadcrumbs?: Array<string | Crumb>;
  title: string;
  lead?: string;
  actions?: ReactNode;
  /** When set, a menu button opens the sidebar drawer (narrow screens). */
  onMenuClick?: () => void;
  /** Drawer state, for the menu button's aria-expanded. */
  menuOpen?: boolean;
}

export function TopBar({
  breadcrumbs = [],
  title,
  lead,
  actions,
  onMenuClick,
  menuOpen = false,
}: TopBarProps) {
  return (
    <header
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 14,
        // --page-px narrows the side padding on small screens (set by the app).
        padding: '32px var(--page-px, 40px) 28px',
        borderBottom: '1px solid var(--gc-line)',
        background: 'var(--gc-cream)',
      }}
    >
      {onMenuClick || breadcrumbs.length > 0 ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
          {onMenuClick ? (
            <button
              type="button"
              aria-label="Open menu"
              aria-expanded={menuOpen}
              onClick={onMenuClick}
              style={{
                width: 40,
                height: 40,
                margin: '-8px 0 -8px -10px',
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'transparent',
                border: 'none',
                borderRadius: 10,
                cursor: 'pointer',
              }}
            >
              <NavIcon name="menu" color="var(--gc-charcoal)" size={22} />
            </button>
          ) : null}
          {breadcrumbs.length > 0 ? (
            <nav
              aria-label="Breadcrumb"
              style={{
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 8,
                minWidth: 0,
                fontFamily: 'var(--font-sans)',
                fontWeight: 600,
                fontSize: 10,
                letterSpacing: 'var(--track-xwide)',
                textTransform: 'uppercase',
                color: 'var(--gc-stone)',
              }}
            >
              {breadcrumbs.map((raw, i) => {
                const crumb = typeof raw === 'string' ? { label: raw } : raw;
                const last = i === breadcrumbs.length - 1;
                return (
                  <Fragment key={`${i}-${crumb.label}`}>
                    {i > 0 ? (
                      <span aria-hidden="true" style={{ color: 'var(--gc-mist)' }}>
                        /
                      </span>
                    ) : null}
                    {crumb.href && !last ? (
                      <a
                        href={crumb.href}
                        onClick={(e) => {
                          if (!crumb.onNavigate) return;
                          if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
                          e.preventDefault();
                          crumb.onNavigate();
                        }}
                        style={{
                          color: 'var(--gc-stone)',
                          textDecoration: 'underline',
                          textDecorationColor: 'var(--gc-line)',
                          textUnderlineOffset: 3,
                        }}
                      >
                        {crumb.label}
                      </a>
                    ) : (
                      <span
                        {...(last ? { 'aria-current': 'page' as const } : {})}
                        style={{ color: last ? 'var(--gc-green-dark)' : 'var(--gc-stone)' }}
                      >
                        {crumb.label}
                      </span>
                    )}
                  </Fragment>
                );
              })}
            </nav>
          ) : null}
        </div>
      ) : null}

      <div
        style={{
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '16px 32px',
        }}
      >
        <div
          style={{ display: 'flex', flexDirection: 'column', gap: 10, maxWidth: 720, minWidth: 0 }}
        >
          <h1
            style={{
              margin: 0,
              fontFamily: 'var(--font-sans)',
              fontWeight: 300,
              fontSize: 'clamp(28px, 5vw, 40px)',
              color: 'var(--fg)',
              letterSpacing: 'var(--track-tight)',
              lineHeight: 1.05,
            }}
          >
            {title}
          </h1>
          {lead ? (
            <p
              style={{
                margin: 0,
                fontFamily: 'var(--font-serif)',
                fontStyle: 'italic',
                fontWeight: 400,
                fontSize: 'clamp(17px, 2.6vw, 20px)',
                color: 'var(--gc-charcoal)',
                lineHeight: 1.4,
              }}
            >
              {lead}
            </p>
          ) : null}
        </div>
        {actions ? (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, flexShrink: 0 }}>{actions}</div>
        ) : null}
      </div>
    </header>
  );
}
